import { Link } from 'react-router-dom';
import { Check, MessageCircle } from 'lucide-react';
import CircuitField from './CircuitField';
import { PRICES, shekel } from '@/data/prices';

const WA = `https://wa.me/972546672863?text=${encodeURIComponent('שלום בניהו, יש לי שאלה:')}`;
const PROMISES = [`בדיקה במעבדה: ${shekel(PRICES.labCheck.min)}`, 'מחיר בכתב לפני שמתחילים', '3 חודשי אחריות על כל תיקון', 'מעבדה בנטור, בתיאום מראש'];

/**
 * Homepage hero (redesigned 2026-10-06).
 *
 * No photo: the Canva banner it used had "תיקון מחשבים בגולן" baked into the
 * image, so the headline showed twice, offset — and the real h1 had no colour
 * of its own and rendered near-black on the dark background. Now: a navy base,
 * two brand-blue glows and a faint grid (all CSS, nothing to download), white
 * type, and buttons made for a dark surface. The LCP element is text again.
 */
const HeroCinematic = () => {
  return (
    <section className="cin-dark font-heebo relative isolate flex flex-col justify-center min-h-[86svh] md:min-h-[80svh] overflow-hidden bg-[#0B1224] pt-28 pb-16 md:pt-32 md:pb-24 text-white">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-[radial-gradient(900px_520px_at_85%_-5%,rgba(30,85,217,0.55),transparent_62%),radial-gradient(700px_460px_at_5%_105%,rgba(76,79,230,0.32),transparent_60%)]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-[0.06] [background-image:linear-gradient(rgba(255,255,255,0.9)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.9)_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_40%,black,transparent)]"
      />
      {/* Live circuit traces that react to the mouse (2026-10-10) — starts after load. */}
      <CircuitField />

      <div className="relative mx-auto grid max-w-[1280px] w-full items-center gap-14 px-5 md:px-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:px-20">
        <div>
        <div className="cin-reveal cin-eager mb-7 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3.5 py-1.5 text-[13px] font-medium text-white/80">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
          מעבדה בנטור · גולן, גליל וטבריה מאז 2018
        </div>

        <h1 className="cin-reveal cin-eager cin-eager-lcp cin-display !text-[clamp(2.5rem,5.2vw,4.75rem)] max-w-4xl mb-6 text-white">
          תיקון מחשבים{' '}
          <span className="text-[#A9BEFF]">בגולן</span>
        </h1>

        <p className="cin-reveal cin-eager cin-eager-lcp max-w-2xl text-lg md:text-xl leading-relaxed text-white/75 mb-10">
          מחשב שהתקלקל או נהיה איטי? כתבו לי ותקבלו ייעוץ חינם: מה כדאי לעשות, ואם בכלל שווה לתקן. את מחיר התיקון מקבלים בכתב, לפני שמתחילים.
        </p>

        <div className="cin-reveal cin-eager cin-reveal-d2 flex flex-col sm:flex-row gap-3 sm:gap-6 sm:items-center">
          {/* 2026-10-20: WhatsApp first — the owner's main action; the free-consultation form second. */}
          <a href={WA} target="_blank" rel="noopener noreferrer" data-track-from="home-hero" className="cin-btn-primary cin-btn-on-dark justify-center gap-2">
            <MessageCircle className="h-5 w-5" aria-hidden="true" />
            כתבו לי בוואטסאפ
          </a>
          <Link to="/diagnose" className="cin-btn-ghost text-white/85 self-center">
            ייעוץ חינם באתר ←
          </Link>
        </div>

        {/* 2026-10-27 (owner): buying a computer here is as visible as fixing one. */}
        <a href="#sold" className="cin-reveal cin-eager cin-reveal-d3 mt-8 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-[14px] font-medium text-white/90 transition-colors hover:bg-emerald-400/20">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
          מחשבים שנמכרו לאחרונה · משלוח לכל הארץ
          <span aria-hidden="true">←</span>
        </a>
        </div>

        {/* Desktop only: what the visitor gets, so the wide hero isn't half empty. */}
        <aside className="cin-reveal cin-eager cin-reveal-d3 hidden lg:block rounded-2xl border border-white/10 bg-white/[0.05] p-7 backdrop-blur-md">
          <div className="mb-6 flex items-center gap-4 border-b border-white/10 pb-6">
            <div className="cin-counter text-5xl font-extrabold leading-none tracking-tight">חינם</div>
            <div>
              <div className="text-[15px] font-semibold text-white/85">שיחת ייעוץ</div>
              <div className="mt-1 text-sm text-white/60">בוואטסאפ, בטלפון או בטופס באתר</div>
            </div>
          </div>
          <ul className="space-y-3.5">
            {PROMISES.map((item) => (
              <li key={item} className="flex items-center gap-3 text-[15px] text-white/85">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[var(--cin-blue)]/30 text-[#A9C1FF]">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </section>
  );
};

export default HeroCinematic;
